/**
 * NDT field tickets (batch 4, fieldiq-api#1157): status skin, scope and unit
 * words, the money column, the rate-card picker's candidates, the PDF filename,
 * who may press what, and every refusal as a sentence.
 *
 * NOTHING HERE MULTIPLIES OR ADDS. A line's amount and its `money` verdict and
 * the ticket's subtotal are the API's; this file only words them. A line with
 * no price reads "No rate" (never $0), an explicit 0 reads "No charge", and a
 * NULL subtotal on a ticket with lines reads "No rate".
 */
import { fmtCurrency } from './display-labels';
import { formatCalendarDate } from './calendar-date';
import { NDT_UNKNOWN_SKIN, canDispatchNdt, canWorkNdt, type NdtSkin } from './ndt-inspection';
import type { NdtLineMoney, NdtManualLineUnit, NdtRate, NdtTicketStatus } from '../types/ndt-tickets';

/**
 * Ticket status. Draft amber (still with the crew), signed blue (the company
 * man signed; the office has not approved), approved green (billable),
 * invoiced green (billed — the label tells them apart), void slate.
 */
export const NDT_TICKET_STATUS_SKIN: Record<NdtTicketStatus, NdtSkin> = {
  draft: { label: 'Draft', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  signed: { label: 'Signed', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  approved: { label: 'Approved', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  invoiced: { label: 'Invoiced', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  void: { label: 'Void', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
};
export const ndtTicketStatusSkin = (v: string | null | undefined): NdtSkin =>
  v && Object.prototype.hasOwnProperty.call(NDT_TICKET_STATUS_SKIN, v) ? NDT_TICKET_STATUS_SKIN[v as NdtTicketStatus] : NDT_UNKNOWN_SKIN;

export const NDT_TICKET_SCOPE_LABEL: Record<string, string> = { day: 'Day', job: 'Whole job', final: 'Final' };

/** What a ticket covers: one work day, the whole job, or the final sweep. Unknown → `—`. */
export function ndtTicketScopeText(scope: string | null | undefined, workDate: string | null | undefined): string {
  if (scope === 'day') return workDate ? `Day ${formatCalendarDate(workDate) ?? workDate}` : 'Day';
  if (scope === 'job') return 'Whole job';
  if (scope === 'final') return 'Final (job completion)';
  return '—';
}

/** The rate-card unit words, as lib/ndt-rates.ts labels them. */
export const NDT_LINE_UNIT_LABEL: Record<string, string> = {
  per_weld: 'Per weld',
  per_shot: 'Per shot / film',
  per_crew_day: 'Per crew-day',
  per_hour: 'Per hour',
  per_distance: 'Per distance',
  flat: 'Flat charge',
  consumable: 'Consumable',
};
export const ndtLineUnitLabel = (u: string | null | undefined): string =>
  u && Object.prototype.hasOwnProperty.call(NDT_LINE_UNIT_LABEL, u) ? NDT_LINE_UNIT_LABEL[u] : '—';

/** What one of a line's quantity is: weld, shot, crew-day, hour, km / mi, each. */
export function ndtUnitBasis(unit: string | null | undefined, distanceUnit?: string | null): string {
  switch (unit) {
    case 'per_weld': return 'weld';
    case 'per_shot': return 'shot';
    case 'per_crew_day': return 'crew-day';
    case 'per_hour': return 'hour';
    case 'per_distance': return distanceUnit === 'mi' ? 'mi' : distanceUnit === 'km' ? 'km' : 'distance';
    case 'flat': return 'each';
    case 'consumable': return 'each';
    default: return '';
  }
}

/** "3 shot", "120 km"; an absent quantity is `—`. */
export function ndtQtyText(quantity: number | null | undefined, unit: string | null | undefined, distanceUnit?: string | null): string {
  if (quantity === null || quantity === undefined || !Number.isFinite(Number(quantity))) return '—';
  const n = Number(quantity).toLocaleString('en-US', { maximumFractionDigits: 2 });
  const b = ndtUnitBasis(unit, distanceUnit);
  return b ? `${n} ${b}` : n;
}

/** A rate as "CA$185.00/shot"; an absent rate is "No rate", never $0. */
export function ndtRateText(rate: number | null | undefined, currency: string | null | undefined, unit: string | null | undefined, distanceUnit?: string | null): string {
  if (rate === null || rate === undefined) return 'No rate';
  const b = ndtUnitBasis(unit, distanceUnit);
  return fmtCurrency(rate, currency, 2, b && b !== 'each' ? `/${b}` : undefined);
}

/** The money column: the API's amount, or its verdict in words. An unknown verdict is `—`. */
export function ndtLineAmountText(line: { money: NdtLineMoney | string | null | undefined; amount: number | null | undefined }, currency: string | null | undefined): string {
  switch (line.money) {
    case 'priced': return fmtCurrency(line.amount, currency);
    case 'no_charge': return 'No charge';
    case 'no_rate': return 'No rate';
    default: return '—';
  }
}

export const ndtLineNeedsPrice = (line: { money: string | null | undefined }) => line.money === 'no_rate';

/** The subtotal the API holds. NULL with lines is "No rate"; no lines is `—`. */
export function ndtSubtotalText(subtotal: number | null | undefined, lineCount: number | null | undefined, currency: string | null | undefined): string {
  if (subtotal !== null && subtotal !== undefined) return fmtCurrency(subtotal, currency);
  return (lineCount ?? 0) > 0 ? 'No rate' : '—';
}

/** The API's own filename (GET /tickets/:id/pdf Content-Disposition): NDT-0001.pdf, -DRAFT, -VOID. */
export function ndtTicketPdfFilename(t: { ticketNumber: string; status: string }): string {
  const suffix = t.status === 'draft' ? '-DRAFT' : t.status === 'void' ? '-VOID' : '';
  return `${t.ticketNumber}${suffix}.pdf`;
}

/** Approve, void, reprice, delete, set a price, pick a rate line: NDT_DISPATCH. */
export const canPriceNdtTickets = canDispatchNdt;
/** Close a day, add from the rate card, change a quantity, sign: NDT_WORK (the API checks the crew). */
export const canWorkNdtTickets = canWorkNdt;

/**
 * The rate-card lines a person may add by hand to this ticket, for this day:
 * a manual unit, in force on the day, this customer's or the company default.
 * A filter of the API's list — nothing priced here.
 */
export function ndtManualRateChoices(rates: NdtRate[], customerId: string | null | undefined): NdtRate[] {
  const manual = ['per_crew_day', 'per_hour', 'per_distance', 'flat', 'consumable'];
  return rates.filter((r) => manual.includes(r.unit) && (r.customerId === null || r.customerId === customerId));
}

/** The rate lines the office may pick for an unpriced line: same unit, this customer's or the default. */
export function ndtRatePickChoices(rates: NdtRate[], unit: string, customerId: string | null | undefined): NdtRate[] {
  return rates.filter((r) => r.unit === unit && (r.customerId === null || r.customerId === customerId));
}

export const NDT_OFFICE_LINE_UNITS: readonly NdtManualLineUnit[] = ['per_hour', 'per_distance', 'consumable', 'flat', 'per_crew_day'];

/**
 * EVERY `code` the ticket routes answer with (routes/ndt-inspection-tickets.routes.ts,
 * lib/ndt-field-tickets.ts, and exam_ticketed from DELETE /exams/:id), as a
 * plain sentence. `ndt-b4.test.tsx` pins the key list against the API's, so a
 * code added there and not here fails a test rather than falling back silently.
 *
 * The codes in PREFER_API_TEXT carry the API's own specifics (the day, the
 * ticket number, the lines without a price), which say more than any sentence
 * here could; for those the API's `error` is shown when it sent one.
 */
export const NDT_TICKET_REFUSAL_MESSAGES: Record<string, string> = {
  invalid_request: 'The request was not valid.',
  reference_not_found: 'That rate line is not on this account.',
  rate_wrong_customer: 'That rate line belongs to another customer.',
  rate_not_in_force: 'That rate line is not in force on this day.',
  exam_unit_manual: 'Per-weld and per-shot lines come from recorded exams. Close out the day to add them.',
  rate_office_only: 'Prices are set by the office. Enter the quantity; the rate comes from the rate card.',
  auto_line_field: 'An automatic line keeps its description. Change its quantity, or remove it with a reason.',
  rate_unit_mismatch: 'That rate line is billed in a different unit from this line.',
  reason_required: 'A reason is required.',
  no_lines: 'This ticket has no charges to sign for. Add a line first.',
  unrated_lines: 'Some lines have no price yet. The office sets a rate (or no charge), or the line is removed.',
  ticket_not_draft: 'This ticket is no longer a draft, so it cannot change. Void it and draft a new one to correct it.',
  invalid_signature: 'The signature was not received. Draw it again and confirm.',
  not_on_crew: "You are not on this job's crew, so you cannot work on its tickets.",
  not_signed: 'Only a signed ticket can be approved.',
  already_invoiced: 'This ticket is invoiced and cannot be voided.',
  already_void: 'This ticket is already void.',
  exam_already_billed: 'That exam is already billed on another ticket.',
  ticket_number_contended: 'Another ticket was being numbered at the same moment. Try again.',
  not_found: 'This ticket was not found. It may have been deleted.',
  job_not_per_day: 'This job is ticketed per job; its ticket is drafted when the job is completed.',
  job_not_active: 'A day is closed out once work on the job has started.',
  work_date_out_of_range: "That day is outside the job's work — before it started, or after today.",
  day_already_ticketed: 'That day is already on a signed ticket.',
  nothing_to_ticket: 'Everything billable is already on a ticket.',
  job_not_complete: 'Tickets are rebuilt here only for a complete job.',
  ticket_exists: 'This job already has a live ticket. Void it first to replace it.',
  exam_ticketed: 'This exam is billed on a field ticket. Void that ticket first.',
};

const PREFER_API_TEXT = new Set(['invalid_request', 'unrated_lines', 'day_already_ticketed', 'work_date_out_of_range', 'nothing_to_ticket', 'ticket_exists', 'exam_ticketed', 'rate_not_in_force', 'rate_unit_mismatch']);

export interface NdtTicketRefusal {
  status: number | null;
  code: string | null;
  message: string;
  /** day_already_ticketed / nothing_to_ticket name the ticket the day is on. */
  ticketId: string | null;
}

export function ndtTicketRefusal(err: unknown, generic: string): NdtTicketRefusal {
  const r = (err as { response?: { status?: unknown; data?: Record<string, unknown> } })?.response;
  const d = (r?.data ?? {}) as Record<string, unknown>;
  const status = typeof r?.status === 'number' ? r.status : null;
  const code = typeof d.code === 'string' ? d.code : status === 404 ? 'not_found' : null;
  const apiError = typeof d.error === 'string' && d.error && d.error !== 'Not found' ? d.error : null;
  const known = !!code && Object.prototype.hasOwnProperty.call(NDT_TICKET_REFUSAL_MESSAGES, code);
  const message =
    known && !(PREFER_API_TEXT.has(code as string) && apiError) ? NDT_TICKET_REFUSAL_MESSAGES[code as string] : apiError ?? generic;
  return { status, code, message, ticketId: typeof d.ticketId === 'string' ? d.ticketId : null };
}

/** A quantity or rate as the API takes it: ≥ 0, at most 2 decimals. Null when blank, NaN when not that. */
export function ndtAmountFromInput(text: string): number | null {
  const t = text.trim();
  if (t === '') return null;
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return NaN;
  return Number(t);
}
